/**
 * From the change log's answer to the History card's rows, and from a graph node to the stable
 * id its history is kept under (ADR-0091). The answer includes the events of the entities merged
 * into this one (its aliases): a mention logged under one is labelled with its name, and a
 * merge, its undo or a dismissed match names the other entity, why and who.
 */
import type { EntityHistoryRow } from '@/components/entity-history/EntityHistoryCard'
import {
  type IdentityChange,
  MERGE_REASONS,
  type MergeReason,
} from '@/components/entity-history/identity-row-text'
import type { PropertyChangeRow } from '@/components/entity-history/row-text'
import { type CaseEventDto, type CaseEventKind, isCaseEventKind } from '@/services/api/case-events'
import { eventDetail, historyActor } from './event-detail'
import { factChangeOf } from './fact-rows'
import { valueText } from './summary-view'

/** The node property that holds an entity's stable id (`UID_PROPERTY` on the server). */
const UID_PROPERTY = '_uid'

/** The entity's stable id; null for a node that has none (a document, a passage, an old entity). */
export function entityUid(properties: Record<string, unknown>): string | null {
  const uid = properties[UID_PROPERTY]
  return typeof uid === 'string' && uid.length > 0 ? uid : null
}

/**
 * A `property_changed` event's key and values; the document the replaced value came from is
 * named when another row of the history names it (`names`: document id → name).
 */
function propertyChangeOf(
  event: CaseEventDto,
  names: ReadonlyMap<string, string>
): PropertyChangeRow | null {
  const { key, previous_document_id: previous } = event.payload
  if (event.kind !== 'property_changed' || typeof key !== 'string') return null
  return {
    key,
    old: valueText(event.payload.old),
    new: valueText(event.payload.new),
    conflict: event.payload.conflict === true,
    ...(event.payload.pinned === true ? { pinned: true } : {}),
    previousDocumentName: typeof previous === 'string' ? (names.get(previous) ?? null) : null,
  }
}

/** Whose history the rows are, and who the case's members are. */
export interface HistoryRowsContext {
  /** The entity the history is of: an event naming another uid arrived under an alias (ADR-0091). */
  uid: string | null
  /** Member display names by user id: who merged, undid or dismissed. */
  memberNames: ReadonlyMap<string, string>
}

const NO_CONTEXT: HistoryRowsContext = { uid: null, memberNames: new Map() }

/** One row per event, in the order the server sent them (newest first). */
export function toHistoryRows(
  events: readonly CaseEventDto[],
  context: HistoryRowsContext = NO_CONTEXT
): EntityHistoryRow[] {
  const names = new Map<string, string>()
  for (const event of events) {
    if (event.document_id && event.document_name) names.set(event.document_id, event.document_name)
  }
  return events.map(event => {
    const kind = isCaseEventKind(event.kind) ? event.kind : null
    return {
      id: event.id,
      kind,
      documentId: event.document_id,
      documentName: event.document_name,
      learnedAt: event.recorded_at,
      detail: eventDetail(event, context),
      property: propertyChangeOf(event, names),
      alias: aliasOf(event, kind, context.uid),
      identity: identityChangeOf(event, kind, context),
      fact: factChangeOf(event, kind, context),
    }
  })
}

const IDENTITY_KINDS: ReadonlySet<CaseEventKind> = new Set([
  'entities_merged',
  'merge_reverted',
  'pair_dismissed',
])

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null

function isMergeReason(value: unknown): value is MergeReason {
  return typeof value === 'string' && (MERGE_REASONS as readonly string[]).includes(value)
}

/** A mention logged under an entity merged into this one: that entity's name then. */
function aliasOf(event: CaseEventDto, kind: CaseEventKind | null, uid: string | null) {
  if (kind !== 'entity_mentioned' || !uid || event.subject_uid === uid) return null
  return text(event.payload.name)
}

/** A merge, its undo or a dismissed match, seen from the entity whose history this is. */
function identityChangeOf(
  event: CaseEventDto,
  kind: CaseEventKind | null,
  { uid, memberNames }: HistoryRowsContext
): IdentityChange | null {
  if (!kind || !IDENTITY_KINDS.has(kind)) return null
  // The events name the survivor (or the first of a dismissed pair) as subject.
  const fromObject = uid !== null && event.object_uid === uid
  const reason = event.payload.reason
  return {
    otherName: text(fromObject ? event.payload.subject_name : event.payload.object_name),
    absorbed: kind === 'entities_merged' && fromObject,
    reason: kind === 'entities_merged' && isMergeReason(reason) ? reason : null,
    actor: historyActor(event.actor, memberNames),
  }
}
